import React, { useState, useEffect } from 'react';
import { getOverrideAnalytics } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '8px' },
  sub: { fontSize: '14px', color: '#718096', marginBottom: '20px' },
  bar: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  select: { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' },
  card: { background: '#fff', padding: '18px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  cardLabel: { fontSize: '12px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  cardValue: { fontSize: '26px', fontWeight: '700', color: '#1a202c' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#2d3748', margin: '24px 0 12px' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f7fafc', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '10px 14px', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#4a5568' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  note: { background: '#ebf8ff', color: '#2b6cb0', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
};

// Legacy batches (archived before retention) carry only their recorded
// override rate - every count renders as a dash rather than a misleading 0.
const fmt = (v) => (v === null || v === undefined ? '-' : v);

export default function OverrideAnalyticsPage() {
  // Batch options come from the all-time response's `batches` list (live +
  // snapshot-retained + not-retained), not from getBatchResults - archived
  // batches have no live submissions, so the Batch Results list would omit
  // exactly the batches the snapshots exist to preserve.
  const [batchList, setBatchList] = useState([]);
  const [batch, setBatch] = useState('all');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    load('all');
  }, []);

  async function load(b) {
    if (!b) { setData(null); return; }
    setLoading(true); setError('');
    try {
      const r = await getOverrideAnalytics(b);
      if (r.error) { setError(r.error); setData(null); }
      else {
        setData(r);
        if (b === 'all' && r.batches) setBatchList(r.batches);
      }
    } catch { setError('Failed to load override analytics.'); }
    finally { setLoading(false); }
  }

  return (
    <div>
      <h1 style={styles.heading}>Override Analytics</h1>
      <p style={styles.sub}>How often graders changed the AI's grades, and where. A high override rate in a category means the AI most often needed correction there.</p>
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.bar}>
        <select style={styles.select} value={batch} onChange={(e) => { setBatch(e.target.value); load(e.target.value); }}>
          <option value="all">All-Time Average</option>
          {batchList.map((b) => (
            <option key={b.batch} value={b.batch} disabled={b.archived && !b.has_data}>
              Batch {b.batch}{b.archived ? (b.legacy ? ' (archived - rate only)' : (b.has_data ? ' (archived)' : ' (not retained)')) : ''}
            </option>
          ))}
        </select>
        {loading && <span style={{ color: '#718096', fontSize: '13px' }}>Loading...</span>}
        {data && data.batch === 'all' && !loading && (
          <span style={{ color: '#718096', fontSize: '13px' }}>
            Rate averaged across {data.rate_batch_count || 0} batches; submission and item counts cover retained batches only.
          </span>
        )}
      </div>

      {data && (
        <>
          {data.note && <div style={styles.note}>{data.note}</div>}
          <div style={styles.cards}>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Graded Submissions</div>
              <div style={styles.cardValue}>{fmt(data.graded_submissions)}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Items Evaluated</div>
              <div style={styles.cardValue}>{fmt(data.total_units)}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Overrides</div>
              <div style={styles.cardValue}>{fmt(data.total_overrides)}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Override Rate</div>
              <div style={{ ...styles.cardValue, color: data.override_rate > 0 ? '#c53030' : '#276749' }}>{data.override_rate}%</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>AI Agreement</div>
              <div style={{ ...styles.cardValue, color: '#276749' }}>{data.agreement_rate}%</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Raised / Lowered</div>
              <div style={styles.cardValue}>{fmt(data.raised)} / {fmt(data.lowered)}</div>
            </div>
          </div>

          <div style={styles.sectionTitle}>By category (where the AI was corrected most)</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Category / Section</th>
                <th style={styles.th}>Overrides</th>
                <th style={styles.th}>Raised</th>
                <th style={styles.th}>Lowered</th>
              </tr>
            </thead>
            <tbody>
              {data.by_category.map((c, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, fontWeight: '600' }}>{c.category}</td>
                  <td style={styles.td}>{c.overrides}</td>
                  <td style={styles.td}>{c.raised}</td>
                  <td style={styles.td}>{c.lowered}</td>
                </tr>
              ))}
              {data.by_category.length === 0 && (
                <tr><td style={{ ...styles.td, textAlign: 'center', color: '#a0aec0' }} colSpan={4}>{data.legacy ? 'Breakdowns are not available for this batch (recorded rate only).' : (batch === 'all' ? 'No overrides recorded yet.' : 'No overrides recorded for this batch.')}</td></tr>
              )}
            </tbody>
          </table>

          <div style={styles.sectionTitle}>By grader</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Grader</th>
                <th style={styles.th}>Overrides Made</th>
              </tr>
            </thead>
            <tbody>
              {data.by_grader.map((g, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, fontWeight: '600' }}>{g.grader}</td>
                  <td style={styles.td}>{g.overrides}</td>
                </tr>
              ))}
              {data.by_grader.length === 0 && (
                <tr><td style={{ ...styles.td, textAlign: 'center', color: '#a0aec0' }} colSpan={2}>{data.legacy ? 'Breakdowns are not available for this batch (recorded rate only).' : (batch === 'all' ? 'No overrides recorded yet.' : 'No overrides recorded for this batch.')}</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
